import React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Lock } from "lucide-react";
import { canPerform, whoCanPerform, actionLabel, useCurrentAdminRole } from "@/lib/permissions";

/**
 * <Gated action="payments.markSent"> ... </Gated>
 *
 * - If the signed-in admin can perform the action: renders the child unchanged.
 * - If not: clones the child element with `disabled` + a muted style, and wraps
 *   it in a Tooltip explaining which roles could do it.
 *
 * Works on any single React element child (Button, button, NavLink, etc.).
 */
export function Gated({ action, role: explicitRole, children, fallback }) {
  const currentRole = useCurrentAdminRole();
  const role = explicitRole || currentRole;
  const allowed = canPerform(action, role);

  if (allowed) return children;
  if (fallback !== undefined) return fallback;

  // Disabled clone: preserve original look but force disabled state and intercept clicks.
  const disabledChild = React.cloneElement(children, {
    disabled: true,
    onClick: (e) => { e.preventDefault(); e.stopPropagation(); },
    "aria-disabled": true,
    "data-permission-denied": true,
    className: `${children.props.className || ""} opacity-50 cursor-not-allowed pointer-events-none`,
    tabIndex: -1,
  });

  const eligibleRoles = whoCanPerform(action);
  const rolesText = eligibleRoles.length === 1
    ? eligibleRoles[0]
    : eligibleRoles.slice(0, -1).join(", ") + " or " + eligibleRoles[eligibleRoles.length - 1];

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* span wrapper because disabled buttons don't fire hover events */}
          <span className="inline-flex" data-testid={`gated-${action.replace(/\./g, "-")}`}>{disabledChild}</span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="flex items-start gap-2">
            <Lock className="size-3.5 mt-0.5 shrink-0" />
            <div className="text-caption leading-relaxed">
              <div className="font-semibold mb-0.5">Permission required</div>
              <div className="opacity-90">
                <span className="font-medium">{actionLabel(action)}</span> requires {rolesText}.
                You&rsquo;re signed in as <span className="font-medium">{role}</span>.
              </div>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
